import React, { useEffect, useState, useCallback } from 'react';

import AkModalDialog from '@atlaskit/modal-dialog';
import { useThemeObserver } from '@atlaskit/tokens';

import { OnboardingModalFooter } from './OnboardingModalFooter';
import { OnboardingModalBody } from './OnboardingModalBody';

// Type declarations
type OnboardingModalProps = {
	slides: OnboardingSlide[];
	onClose: () => void;
};

export type OnboardingSlide = {
	title: JSX.Element;
	body: JSX.Element;
	imageUrlLight: string;
	imageUrlDark: string;
	isTransparentBackground?: boolean;
	learnMoreUrl?: string;
};

export function OnboardingModal({ slides, onClose }: OnboardingModalProps) {
	// States
	const [slideIndex, setSlideIndex] = useState(0);
	const theme = useThemeObserver();

	// Image preloader
	useEffect(() => {
		slides.forEach((slide) => {
			if (slide.imageUrlLight || slide.imageUrlDark) {
				const preloadedImage = new Image();
				preloadedImage.src =
					theme.colorMode === 'dark' ? slide.imageUrlDark! : slide.imageUrlLight!;
			}
		});
	}, [slides, theme]);

	// Helpers
	const isLastSlide = slideIndex + 1 === slides.length;
	const isFirstSlide = slideIndex === 0;

	// Callbacks (memoized)
	const onPrev = useCallback(() => {
		!isFirstSlide && setSlideIndex(slideIndex - 1);
	}, [isFirstSlide, setSlideIndex, slideIndex]);
	const onSkip = useCallback(onClose, [onClose]);
	const onNext = useCallback(() => {
		isLastSlide ? onClose() : setSlideIndex(slideIndex + 1);
	}, [isLastSlide, onClose, setSlideIndex, slideIndex]);

	// UI functions
	return (
		<AkModalDialog onClose={onClose} width={550}>
			<OnboardingModalBody
				selectedIndex={slideIndex}
				isLastSlide={isLastSlide}
				isFirstSlide={isFirstSlide}
				onPrev={onPrev}
				onNext={onNext}
				slides={slides}
			/>
			<OnboardingModalFooter
				selectedIndex={slideIndex}
				isLastSlide={isLastSlide}
				onSkip={onSkip}
				onNext={onNext}
				slides={slides}
			/>
		</AkModalDialog>
	);
}
